/**
 * TenantBootcampEnrollment.jsx
 *
 * Bootcamp enrollment form exclusively for Student Tenant Login users.
 * - Auto-fills Full Name and Roll Number from the student's profile record.
 * - Defaults experience to "0-2 years" (students are fresh/early-career).
 * - Removes professional/employee fields (phone, DOB, gender, address, company,
 *   working-professional section).
 * - Adds student-specific fields: Branch, Interested Role, Interested Skill,
 *   Previous Skill.
 *
 * The original bootcamp1.jsx (Learner Login) is left completely untouched.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Chip,
  Collapse,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  RocketLaunch as RocketIcon,
  Psychology as PsychologyIcon,
  Terminal as TerminalIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Build as BuildIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import API_BASE_URL from "../Config/config";
import {
  STUDENT_TENANT_ROLES,
  STUDENT_TENANT_ROLE_VALUES,
} from "../Config/tenantRoleConfig";

/* ------------------------------- Styling ------------------------------- */

const sxField = {
  "& .MuiInputBase-root": {
    backgroundColor: "#FFFFFF",
    color: "var(--text-heading)",
    borderRadius: "16px",
    fontWeight: 600,
    transition: "all 0.2s ease",
    "& input": { color: "var(--text-heading)" },
    "& fieldset": {
      borderColor: "#E2E8F0",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--primary-blue)",
      borderWidth: "2px",
    },
  },
  "& .MuiFormLabel-root": {
    color: "#64748B",
    fontWeight: 600,
    fontSize: "0.95rem",
    "&.Mui-focused": { color: "var(--primary-blue)" },
  },
  "& svg": { color: "var(--primary-blue)" },
};

const sxReadOnly = {
  ...sxField,
  "& .MuiInputBase-root": {
    ...sxField["& .MuiInputBase-root"],
    backgroundColor: "#F8FAFC",
    "& fieldset": { borderColor: "#E2E8F0" },
  },
};

/* ----------------------------- Sub-component ---------------------------- */

function SelectField({
  name,
  label,
  value,
  onChange,
  required = false,
  children,
  placeholder = "- Select -",
}) {
  const labelId = `${name}-label`;
  return (
    <FormControl required={required} fullWidth margin="normal" sx={sxField}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        label={label}
      >
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
        {children}
      </Select>
    </FormControl>
  );
}

/* ------------------------------- Static data ---------------------------- */

/**
 * Common B.Tech / degree branches.
 * To add more branches, simply append to this array - no other code changes needed.
 */
const BRANCH_OPTIONS = [
  "Computer Science & Engineering (CSE)",
  "Information Technology (IT)",
  "Electronics & Communication Engineering (ECE)",
  "Electrical & Electronics Engineering (EEE)",
  "Mechanical Engineering",
  "Civil Engineering",
  "Chemical Engineering",
  "Biotechnology",
  "Artificial Intelligence & Machine Learning (AI/ML)",
  "Data Science",
  "Cyber Security",
  "Internet of Things (IoT)",
  "Aerospace Engineering",
  "Automobile Engineering",
  "Industrial Engineering",
  "Mining Engineering",
  "Petroleum Engineering",
  "Agricultural Engineering",
  "Environmental Engineering",
  "Other",
];

const TRACK_OPTIONS = [
  {
    value: "techbunch",
    label: "Full Stack Engineering Path",
    description: "Build production-grade applications with modern web stacks.",
    icon: <TerminalIcon />,
    color: "#6366F1",
    gradient: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
    bgGradient: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
    tags: ["Web Dev", "Full Stack", "Projects"],
  },
  {
    value: "roles",
    label: "Industry Career Accelerator",
    description: "Fast-track your career with role-specific professional training.",
    icon: <RocketIcon />,
    color: "#7C3AED",
    gradient: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
    bgGradient: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
    tags: ["Career", "Role-based", "Industry"],
  },
];

/**
 * TECH_BUNCH_TRACKS - single source of truth for all tech-bunch entries.
 *
 * title    -> shown in the dropdown list (what the student reads when choosing)
 * skillSet -> shown in the selected-card header AND stored/submitted as the value
 *            (must stay in sync with TECH_BUNCH_DETAILS keys for detail lookup)
 *
 * Backward-compat: legacy saved values are the raw skillSet strings, so using
 * skillSet as the stored value means old records continue to work without migration.
 */
const TECH_BUNCH_TRACKS = [
  {
    title:    "Full Stack RAG Developer",
    skillSet: "React.js + Node.js + PostgreSQL + RAG Basics",
    folder:   "react-node-postgresql-rag-basics",
  },
  {
    title:    "Python Backend Developer",
    skillSet: "Python + Django + REST API + PostgreSQL",
    folder:   "python-django-postgresql",
  },
  {
    title:    "SAP Full Stack Developer",
    skillSet: "React.js + SAP APIs + Node.js + PostgreSQL",
    folder:   "react-sap-node-postgresql",
  },
  {
    title:    "Enterprise SAP Fiori Developer",
    skillSet: "SAP Fiori + Java Spring Boot + Microservices + MySQL",
    folder:   "fiori-java-spring-boot",
  },
  {
    title:    "Cross-Platform App Developer",
    skillSet: "Flutter + Riverpod + Node.js + Express.js + PostgreSQL",
    folder:   "flutter-node-postgresql",
  },
  {
    title:    "AI / ML Engineer",
    skillSet: "TensorFlow + Keras + PyTorch + Scikit-learn + FastAPI + Hugging Face Transformers",
    folder:   "ai-ml-tensorflow-pytorch-fastapi",
  },
  {
    title:    "Realtime Node.js Developer",
    skillSet: "Node.js + Express.js + PostgreSQL + Redis + WebSockets",
    folder:   "node-express-postgresql-redis",
  },
  {
    title:    "FastAPI Vector AI Developer",
    skillSet: "FastAPI + MongoDB + Vector Database + LangChain",
    folder:   "fastapi-mongodb-vectordb-langchain",
  },
];

/**
 * Quick lookup: skillSet string -> friendly title.
 * Used for legacy values stored before TECH_BUNCH_TRACKS existed.
 */
const SKILL_SET_TO_TITLE = Object.fromEntries(
  TECH_BUNCH_TRACKS.map(({ title, skillSet }) => [skillSet, title])
);

/**
 * Rich per-track content displayed in the expandable detail card.
 * Keys must exactly match the `skillSet` field in TECH_BUNCH_TRACKS.
 * Add new tracks here - the card component reads this map automatically.
 */
const TECH_BUNCH_DETAILS = {
  "React.js + Node.js + PostgreSQL + RAG Basics": {
    title: "Full Stack RAG Developer",
    shortSummary:
      "Build AI-powered web apps that can search documents, answer questions, and generate smart responses - using React on the frontend and a Node.js + AI backend.",
    whatYouWillLearn: [
      "React.js - build dynamic, responsive user interfaces",
      "Node.js + Express - create fast, scalable REST APIs",
      "PostgreSQL - design and query relational databases",
      "RAG (Retrieval-Augmented Generation) - connect your app to AI models that understand real data",
      "pgvector - store and search AI embeddings in PostgreSQL",
      "Prompt engineering basics - write effective prompts for LLMs",
    ],
    whatYouWillBuild: [
      "An AI document Q&A system (like a smart chatbot for your own files)",
      "A full-stack job portal with AI-powered search",
      "A knowledge base app that summarises and retrieves content intelligently",
      "A portfolio project showcasing RAG pipelines deployed on a live URL",
    ],
    realWorldUseCases: [
      "Customer support bots that answer from real company documentation",
      "Internal knowledge search tools used by enterprises",
      "Resume screening tools that match candidates to job descriptions",
      "AI-powered study assistants for EdTech platforms",
    ],
    companySuggestions: [
      "OpenAI", "Notion", "Glean", "Guru", "Perplexity AI",
      "Accenture AI", "TCS iON", "Infosys Topaz", "Zoho",
    ],
    careerOutcome:
      "You will be job-ready for Full Stack Developer and AI Application Developer roles. Companies building AI-first products actively hire engineers who understand both modern React/Node.js development and how to integrate LLMs into real applications.",
    tags: ["AI", "Full Stack", "RAG", "React", "Node.js", "PostgreSQL"],
  },

  "Python + Django + REST API + PostgreSQL": {
    title: "Python Backend Developer",
    shortSummary:
      "Master server-side development with Python and Django - one of the most in-demand backend stacks globally, used by startups and large enterprises alike.",
    whatYouWillLearn: [
      "Python - clean, readable, industry-standard programming",
      "Django - build production-grade web applications fast",
      "Django REST Framework - design RESTful APIs for mobile and web apps",
      "PostgreSQL - relational database design, queries, and optimisation",
      "Authentication & security - JWT, session management, permissions",
      "API testing - Postman, unit tests, and integration tests",
    ],
    whatYouWillBuild: [
      "A RESTful API backend for an e-commerce platform",
      "A Django-powered content management system (CMS)",
      "A multi-user task management app with role-based access",
      "A real-time data dashboard connected to a PostgreSQL database",
    ],
    realWorldUseCases: [
      "Backend services for fintech and banking apps",
      "Government portals and internal enterprise tools",
      "SaaS platforms with complex data models and user roles",
      "Data pipelines and automation tools for analytics teams",
    ],
    companySuggestions: [
      "Instagram (Meta)", "Pinterest", "Disqus", "Spotify", "Dropbox",
      "Freshworks", "Razorpay", "MakeMyTrip", "Capgemini",
    ],
    careerOutcome:
      "Python and Django developers are consistently in the top 5 most-hired backend roles. After this track you will be able to design, build, and deploy secure, scalable APIs - a skill valued in product companies, IT services, and freelance work.",
    tags: ["Python", "Django", "REST API", "Backend", "PostgreSQL"],
  },

  "React.js + SAP APIs + Node.js + PostgreSQL": {
    title: "SAP Full Stack Developer",
    shortSummary:
      "Combine modern web development with SAP enterprise integrations - a rare and highly valued skill set that opens doors in large enterprises running SAP systems.",
    whatYouWillLearn: [
      "React.js - build professional UI components and dashboards",
      "Node.js + Express - create middleware APIs that talk to SAP backends",
      "SAP OData & REST APIs - read and write SAP business data",
      "SAP BTP basics - understand the SAP Business Technology Platform",
      "PostgreSQL - store app-level data alongside SAP data",
      "Authentication with SAP SSO and OAuth 2.0",
    ],
    whatYouWillBuild: [
      "A React dashboard that displays live SAP sales and inventory data",
      "A custom approval workflow app integrated with SAP modules",
      "A Node.js middleware service that bridges SAP and modern web apps",
      "A procurement portal that reads and writes SAP data via APIs",
    ],
    realWorldUseCases: [
      "Custom employee self-service portals at large manufacturing companies",
      "Finance dashboards that pull live data from SAP ERP",
      "Mobile-friendly extensions on top of legacy SAP installations",
      "Procurement and supply chain visibility tools",
    ],
    companySuggestions: [
      "SAP SE", "IBM", "Deloitte", "Accenture", "Capgemini",
      "Wipro", "HCL Technologies", "L&T Infotech", "Infosys",
    ],
    careerOutcome:
      "SAP integration developers are rare and command premium salaries in enterprise IT. This track positions you for SAP Developer, Integration Engineer, and Full Stack Enterprise Developer roles at companies running SAP-based operations.",
    tags: ["SAP", "Full Stack", "React", "Node.js", "Enterprise"],
  },

  "SAP Fiori + Java Spring Boot + Microservices + MySQL": {
    title: "Enterprise SAP Fiori Developer",
    shortSummary:
      "Step into enterprise-grade development - build SAP Fiori UIs and Java microservices that power the business operations of Fortune 500 companies.",
    whatYouWillLearn: [
      "SAP Fiori & UI5 - build enterprise UX following SAP design standards",
      "Java Spring Boot - develop robust backend microservices",
      "Microservices architecture - decompose large systems into independent services",
      "MySQL - design and optimise relational databases for enterprise workloads",
      "REST and OData APIs - connect Fiori apps to backend services",
      "Docker basics - containerise and run your microservices locally",
    ],
    whatYouWillBuild: [
      "A Fiori-styled employee leave management application",
      "A Spring Boot microservice for order processing",
      "A product catalogue system with Fiori UI and MySQL backend",
      "A multi-service enterprise app deployed using Docker Compose",
    ],
    realWorldUseCases: [
      "HR, payroll, and leave management portals at large corporations",
      "Logistics and warehouse management systems",
      "Finance and procurement workflows at manufacturing firms",
      "Customer-facing service portals at telecom and utility companies",
    ],
    companySuggestions: [
      "SAP SE", "Siemens", "Bosch", "Tata Consultancy Services",
      "Wipro", "Infosys", "DXC Technology", "Accenture", "NTT Data",
    ],
    careerOutcome:
      "Enterprise SAP Fiori developers work on mission-critical systems for global corporations. This track prepares you for SAP Fiori Developer, Java Microservices Developer, and Enterprise Application Developer roles - with strong salary prospects in consulting and IT services.",
    tags: ["SAP Fiori", "Java", "Spring Boot", "Microservices", "Enterprise"],
  },

  "Flutter + Riverpod + Node.js + Express.js + PostgreSQL": {
    title: "Cross-Platform App Developer",
    shortSummary:
      "Build beautiful mobile apps for Android and iOS from a single codebase using Flutter, and power them with a real Node.js backend - one stack, every platform.",
    whatYouWillLearn: [
      "Flutter & Dart - create pixel-perfect apps for iOS, Android, and web",
      "Riverpod - manage app state cleanly and predictably",
      "Node.js + Express - build the backend APIs your app will consume",
      "PostgreSQL - store user data, sessions, and app content",
      "REST API integration - connect your Flutter app to any backend",
      "App navigation, animations, and responsive layouts",
    ],
    whatYouWillBuild: [
      "A full-stack food delivery app with real-time order tracking",
      "A social media feed app with user authentication",
      "A task manager with offline support and cloud sync",
      "A cross-platform e-commerce app with a Node.js backend",
    ],
    realWorldUseCases: [
      "Consumer apps at startups that need both iOS and Android quickly",
      "Internal tools for field teams at logistics and delivery companies",
      "Healthcare apps that run on tablets and phones",
      "EdTech apps used by students on all device types",
    ],
    companySuggestions: [
      "Google", "Alibaba", "BMW", "eBay Motors", "Nubank",
      "Swiggy", "Zepto", "Dream11", "Cure.fit", "PhonePe",
    ],
    careerOutcome:
      "Flutter is among the fastest-growing mobile frameworks. This track makes you a cross-platform mobile developer - one of the most sought-after roles at product startups and app agencies who need fast, high-quality apps without hiring two separate teams.",
    tags: ["Flutter", "Mobile", "Cross-Platform", "Node.js", "Full Stack"],
  },

  "TensorFlow + Keras + PyTorch + Scikit-learn + FastAPI + Hugging Face Transformers": {
    title: "AI / ML Engineer",
    shortSummary:
      "Go deep into machine learning and AI - from classical algorithms to deep learning and large language models - and learn to deploy your models as real APIs.",
    whatYouWillLearn: [
      "Scikit-learn - classification, regression, clustering, model evaluation",
      "TensorFlow + Keras - build and train neural networks",
      "PyTorch - flexible deep learning for research and production",
      "Hugging Face Transformers - use and fine-tune state-of-the-art LLMs",
      "FastAPI - deploy ML models as production-ready REST APIs",
      "Data preprocessing, feature engineering, and model optimisation",
    ],
    whatYouWillBuild: [
      "An image classifier using CNNs (e.g. plant disease detection)",
      "A sentiment analysis API using a fine-tuned Hugging Face model",
      "A recommendation engine for a mock e-commerce site",
      "A deployed FastAPI service that serves predictions in real time",
    ],
    realWorldUseCases: [
      "Fraud detection systems at banks and payment providers",
      "Product recommendation engines at e-commerce platforms",
      "Medical image analysis tools at healthcare companies",
      "NLP models for customer feedback analysis and routing",
    ],
    companySuggestions: [
      "Google DeepMind", "OpenAI", "Hugging Face", "Amazon AWS AI",
      "Microsoft Azure AI", "Fractal Analytics", "Mu Sigma",
      "Tiger Analytics", "Sigmoid", "Quantiphi",
    ],
    careerOutcome:
      "AI/ML engineers are among the highest-paid tech professionals today. This track prepares you for Machine Learning Engineer, AI Engineer, and Data Scientist roles - with hands-on experience deploying models that actually work in production.",
    tags: ["AI", "Machine Learning", "Deep Learning", "Python", "FastAPI"],
  },

  "Node.js + Express.js + PostgreSQL + Redis + WebSockets": {
    title: "Realtime Node.js Developer",
    shortSummary:
      "Specialise in high-performance, real-time backend systems - the kind that power chat apps, live notifications, and multiplayer experiences at scale.",
    whatYouWillLearn: [
      "Node.js + Express - build event-driven backend services",
      "WebSockets (Socket.IO) - enable real-time bidirectional communication",
      "Redis - implement caching, pub/sub messaging, and session stores",
      "PostgreSQL - design schemas for high-traffic, concurrent workloads",
      "Queue-based architecture - process background jobs reliably",
      "API rate limiting, load handling, and basic horizontal scaling concepts",
    ],
    whatYouWillBuild: [
      "A real-time group chat application with rooms and presence indicators",
      "A live sports score or stock price dashboard using WebSockets",
      "A notification service with Redis pub/sub",
      "A backend API with Redis caching that handles high traffic efficiently",
    ],
    realWorldUseCases: [
      "Live chat features in e-commerce and support platforms",
      "Real-time collaboration tools like Figma or Google Docs",
      "Gaming leaderboards and multiplayer backend systems",
      "IoT dashboards that stream sensor data in real time",
    ],
    companySuggestions: [
      "Slack", "Discord", "Twitch", "Zerodha", "ShareChat",
      "Meesho", "PhonePe", "Hotstar", "Ola", "InMobi",
    ],
    careerOutcome:
      "Real-time backend skills are niche and highly valued. After this track, you will be confident building scalable event-driven systems - making you a strong candidate for Backend Developer, Platform Engineer, and Systems Developer roles at product companies.",
    tags: ["Node.js", "Real-time", "WebSockets", "Redis", "Backend"],
  },

  "FastAPI + MongoDB + Vector Database + LangChain": {
    title: "FastAPI Vector AI Developer",
    shortSummary:
      "Build the backbone of modern AI applications - learn how to store, search, and retrieve information using vector databases and orchestrate LLM workflows with LangChain.",
    whatYouWillLearn: [
      "FastAPI - build fast, type-safe Python APIs with automatic documentation",
      "MongoDB - work with flexible document-based data storage",
      "Vector Databases (Pinecone / Qdrant / Weaviate) - store and query semantic embeddings",
      "LangChain - chain together LLM calls, tools, and memory for complex AI workflows",
      "Embeddings - convert text and data into vector representations for semantic search",
      "RAG pipelines - build systems that retrieve and synthesise information intelligently",
    ],
    whatYouWillBuild: [
      "A semantic search engine for a document library",
      "An AI agent that can answer questions using your own database",
      "A LangChain-powered research assistant with memory and tool use",
      "A FastAPI backend for an AI SaaS product with vector search",
    ],
    realWorldUseCases: [
      "AI copilots built on top of company knowledge bases",
      "Legal and compliance tools that search large document sets intelligently",
      "Healthcare information retrieval systems",
      "Developer tools and IDEs with AI-powered code search",
    ],
    companySuggestions: [
      "Pinecone", "Weaviate", "Cohere", "LangChain AI", "Anthropic",
      "Microsoft (Copilot)", "Salesforce Einstein", "Freshworks Neo",
      "ThoughtSpot", "Kore.ai",
    ],
    careerOutcome:
      "Vector AI and LangChain skills are among the most future-proof skills in software today. This track positions you for AI Engineer, LLM Application Developer, and GenAI Backend Developer roles - at the frontier of what companies are building right now.",
    tags: ["GenAI", "LangChain", "Vector DB", "FastAPI", "MongoDB"],
  },
};

/** Fallback for unknown or legacy tech bunch values */
const TECH_BUNCH_FALLBACK = {
  title: "Full Stack Engineering Path",
  shortSummary:
    "A comprehensive engineering-focused bootcamp track designed to help students build real-world frontend, backend, database, API integration, deployment, and software development skills through practical implementation and industry-oriented learning.",
  whatYouWillLearn: [
    "Frontend & Backend Development",
    "Real-world Projects",
    "APIs & Databases",
    "Deployment & Engineering Practices",
    "Full Stack Application Development",
  ],
  whatYouWillBuild: [
    "A multi-tier web application from scratch",
    "API-driven features with a live database backend",
    "A deployable project to add to your portfolio",
  ],
  realWorldUseCases: [
    "Used across software product companies and IT services",
    "Applied in startup MVPs and enterprise platforms alike",
  ],
  companySuggestions: [
    "TCS", "Infosys", "Wipro", "Accenture", "Cognizant",
    "Product startups", "IT service firms",
  ],
  careerOutcome:
    "You will gain practical project experience that demonstrates your ability to build real software - the most important thing hiring managers look for in early-career developers.",
  tags: ["Full Stack", "Development"],
};

// Student tenant role list is sourced entirely from tenantRoleConfig.js.
// Do NOT add learner roles here - use bootcamp1.jsx for the learner flow.

const SKILL_OPTIONS = [
  "JavaScript", "Python", "Java", "React", "Node.js", "SQL",
  "Machine Learning", "Data Analysis", "Cloud Computing",
  "DevOps", "Cybersecurity", "Embedded Systems", "Mobile Development",
  "UI/UX Design", "Blockchain", "Other",
];

/* ------------------------------- Helpers -------------------------------- */

const enKey = (uid, track) =>
  `tenant_enrollment_${String(uid || "")}_${String(track || "").toLowerCase()}`;

/** Read the logged-in student's profile out of localStorage. */
function readStudentProfile() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return {};
    const u = JSON.parse(raw);
    return {
      userId: u.userId || u.user_id || u.id || "",
      fullName: u.name || u.full_name || u.fullName || "",
      rollNumber: u.roll_number || u.rollNumber || "",
      branch: u.branch || "",
    };
  } catch {
    return {};
  }
}

function isTrackComplete(form) {
  switch (form.track) {
    case "techbunch":    return !!form.techBunch;
    case "roles":        return !!form.role;
    default:             return false;
  }
}

/* -------------------- Tech Bunch Detail Card --------------------------- */

const SECTION_CONFIG = [
  {
    key: "whatYouWillLearn",
    icon: <SchoolIcon sx={{ fontSize: "1.2rem" }} />,
    label: "What You Will Learn",
    accent: "var(--primary-blue, #4F46E5)",
    bg: "rgba(79, 70, 229,0.06)",
    border: "#BFDBFE",
  },
  {
    key: "whatYouWillBuild",
    icon: <BuildIcon sx={{ fontSize: "1.2rem" }} />,
    label: "What You Will Build",
    accent: "#8B5CF6",
    bg: "rgba(139,92,246,0.06)",
    border: "#DDD6FE",
  },
  {
    key: "realWorldUseCases",
    icon: <PublicIcon sx={{ fontSize: "1.2rem" }} />,
    label: "Where This Is Used in Real Life",
    accent: "#10B981",
    bg: "rgba(16,185,129,0.06)",
    border: "#A7F3D0",
  },
  {
    key: "careerOutcome",
    icon: <RocketIcon sx={{ fontSize: "1.2rem" }} />,
    label: "Why This Track Matters for Your Career",
    accent: "#F59E0B",
    bg: "rgba(245,158,11,0.06)",
    border: "#FDE68A",
    isParagraph: true,
  },
];

function TrackSelectionCards({ value, onChange, disabled, existingTrack }) {
  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {disabled && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: "12px",
            backgroundColor: "#FFFBEB",
            border: "1px solid #FDE68A",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <InfoIcon sx={{ color: "#D97706" }} />
          <Typography
            variant="body2"
            sx={{ color: "#92400E", fontWeight: 600, lineHeight: 1.5 }}
          >
            You are already registered for a bootcamp program. To change your
            bootcamp, please contact the administrator.
          </Typography>
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: "#64748B",
          mb: 3,
          fontWeight: 500,
          fontSize: "0.9rem",
          textAlign: "center",
        }}
      >
        Choose the learning track that best aligns with your career goals and long-term skill development.
      </Typography>

      <Grid container spacing={3}>
        {TRACK_OPTIONS.map((opt) => {
          const isSelected = value === opt.value || existingTrack === opt.value;
          const isActuallyDisabled = disabled && existingTrack !== opt.value;

          return (
            <Grid item xs={12} md={6} key={opt.value}>
              <Box
                onClick={() => !disabled && onChange(opt.value)}
                sx={{
                  cursor: disabled ? "default" : "pointer",
                  borderRadius: "20px",
                  border: "2px solid",
                  borderColor: isSelected ? opt.color : "transparent",
                  background: isSelected ? opt.bgGradient : "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                  opacity: isActuallyDisabled ? 0.5 : 1,
                  boxShadow: isSelected
                    ? `0 8px 32px ${opt.color}30, 0 2px 8px ${opt.color}15`
                    : "0 2px 8px rgba(0,0,0,0.06)",
                  "&:hover": {
                    transform: disabled ? "none" : "translateY(-4px)",
                    boxShadow: disabled 
                      ? (isSelected ? `0 8px 32px ${opt.color}30, 0 2px 8px ${opt.color}15` : "0 2px 8px rgba(0,0,0,0.06)")
                      : (isSelected ? `0 16px 40px ${opt.color}40` : "0 8px 24px rgba(0,0,0,0.12)"),
                    borderColor: isSelected ? opt.color : (disabled ? "transparent" : `${opt.color}60`),
                    background: isSelected ? opt.bgGradient : (disabled ? "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)" : "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)"),
                  },
                }}
              >
                {/* Top accent bar */}
                <Box
                  sx={{
                    height: 4,
                    background: opt.gradient,
                    width: "100%",
                    opacity: isSelected ? 1 : 0.4,
                    transition: "opacity 0.3s ease",
                  }}
                />

                {/* Selected badge */}
                {isSelected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      background: opt.gradient,
                      color: "#FFFFFF",
                      borderRadius: "20px",
                      px: 1.5,
                      py: 0.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      boxShadow: `0 2px 8px ${opt.color}40`,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 13 }} />
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.04em" }}>
                      SELECTED
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 3 }}>
                  {/* Icon + Title row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        background: isSelected ? opt.gradient : "#FFFFFF",
                        color: isSelected ? "#FFFFFF" : opt.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: isSelected
                          ? `0 4px 16px ${opt.color}40`
                          : `0 2px 8px ${opt.color}20`,
                        border: isSelected ? "none" : `1.5px solid ${opt.color}30`,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {React.cloneElement(opt.icon, { sx: { fontSize: 28 } })}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: isSelected ? opt.color : "#1E293B",
                          fontSize: "1rem",
                          lineHeight: 1.3,
                          transition: "color 0.3s ease",
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: isSelected ? "#374151" : "#64748B",
                      lineHeight: 1.6,
                      fontSize: "0.875rem",
                      mb: 2,
                    }}
                  >
                    {opt.description}
                  </Typography>

                  {/* Tags */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {opt.tags.map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: 1.5,
                          py: 0.4,
                          borderRadius: "8px",
                          background: isSelected ? `${opt.color}18` : "rgba(100,116,139,0.08)",
                          color: isSelected ? opt.color : "#475569",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.03em",
                          border: `1px solid ${isSelected ? `${opt.color}30` : "transparent"}`,
                        }}
                      >
                        {tag}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function TrackLockWarning() {
  return (
    <Box
      sx={{
        mt: 3,
        p: 2.5,
        borderRadius: "16px",
        backgroundColor: "#F8FAFC",
        border: "1px solid #E2E8F0",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <InfoIcon sx={{ color: "var(--primary-blue, #4F46E5)", mt: 0.2 }} />
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}
        >
          Important: Selection Lock
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#64748B", lineHeight: 1.6, fontSize: "0.875rem" }}
        >
          Once your bootcamp track is submitted, it will be locked and cannot be modified directly.
          If changes are required later, please contact your institution administrator.
        </Typography>
      </Box>
    </Box>
  );
}

function IndustryRoleDetailCard({ role }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        borderRadius: "20px",
        overflow: "hidden",
        border: "1px solid #E2E8F0",
        boxShadow: "0 4px 24px rgba(5,150,105,0.08)",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F9FFF8 100%)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          height: 4,
          width: "100%",
          background: "linear-gradient(90deg, #059669 0%, #10B981 100%)",
        }}
      />

      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
          <Box>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
                color: "#059669",
                fontSize: "0.68rem",
                display: "block",
                mb: 0.5,
              }}
            >
              Accelerator Track
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#0F172A", fontSize: "1.1rem" }}
            >
              {role}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: 700,
              background: "#ECFDF5",
              color: "#065F46",
              border: "1px solid #D1FAE5",
            }}
          >
            Job-Ready Path
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#475569", lineHeight: 1.7, mb: 3, fontSize: "0.95rem" }}
            >
              A career-focused acceleration track designed to prepare students for industry job roles through structured learning paths, role-specific skill development, practical exposure, interview readiness, and placement-oriented training.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1E293B", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 18, color: "#059669" }} /> Career Highlights
                </Typography>
                {[
                  "Industry Role Preparation",
                  "Placement Readiness",
                  "Role-Based Learning Paths",
                  "Career Skill Development",
                  "Professional Growth & Interview Readiness",
                ].map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#059669" }} />
                    <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>{item}</Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: "16px", backgroundColor: "#F0FDF4", border: "1px solid #DCFCE7" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <PsychologyIcon sx={{ fontSize: 18 }} /> Outcome Focus
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#166534", opacity: 0.8, lineHeight: 1.6 }}>
                    This track focuses on transforming academic knowledge into professional expertise, ensuring you are ready to hit the ground running in top-tier technology companies.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function TechBunchDetailCard({ techBunch }) {
  const [expanded, setExpanded] = useState(true);
  const detail = TECH_BUNCH_DETAILS[techBunch];

  if (!detail) return null;

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        borderRadius: "20px",
        overflow: "hidden",
        border: "1px solid #E2E8F0",
        boxShadow: "0 4px 24px rgba(79, 70, 229,0.08)",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          height: 4,
          width: "100%",
          background: "linear-gradient(90deg, var(--primary-blue, #4F46E5) 0%, var(--primary-blue, #4F46E5) 100%)",
        }}
      />

      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.5,
          }}
        >
          {/* Main info */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
                color: "var(--primary-blue, #4F46E5)",
                fontSize: "0.68rem",
                display: "block",
                mb: 0.5,
              }}
            >
              Selected Learning Track
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#0F172A", fontSize: "1.1rem", mb: 1 }}
            >
              {detail.title}
            </Typography>

            {/* Short description */}
            <Typography
              variant="body2"
              sx={{
                color: "#475569",
                lineHeight: 1.7,
                fontSize: "0.875rem",
              }}
            >
              {detail.shortSummary}
            </Typography>
          </Box>
        </Box>

        {/* Expand / Collapse toggle */}
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="tech-bunch-detail-panel"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8rem",
              px: 2,
              py: 0.75,
              color: "var(--primary-blue, #4F46E5)",
              border: "1.5px solid #BFDBFE",
              background: expanded ? "#EFF6FF" : "#FFFFFF",
              "&:hover": { background: "#EFF6FF", borderColor: "#93C5FD" },
              transition: "all 0.2s ease",
            }}
          >
            {expanded ? "Show Less \u2191" : "View Full Details \u2193"}
          </Button>
          {!expanded && (
            <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.75rem" }}>
              Learn what you'll build, companies that hire, and career outcomes
            </Typography>
          )}
        </Box>
      </Box>

      {/* Expandable full details */}
      <Collapse in={expanded} id="tech-bunch-detail-panel">
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            pb: 3,
            pt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Divider */}
          <Box sx={{ height: 1, background: "#E2E8F0", borderRadius: 1 }} />

          {/* Dynamic sections */}
          {SECTION_CONFIG.map(({ key, icon, label, accent, bg, border, isParagraph }) => {
            const content = detail[key];
            if (!content) return null;
            return (
              <Box
                key={key}
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${border}`,
                  background: bg,
                  p: { xs: 2, md: 2.5 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                  <Box component="span" sx={{ fontSize: "1rem", lineHeight: 1 }}>{icon}</Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: accent, fontSize: "0.8rem", letterSpacing: "0.02em" }}
                  >
                    {label}
                  </Typography>
                </Box>
                {isParagraph ? (
                  <Typography variant="body2" sx={{ color: "#0F172A", lineHeight: 1.75, fontSize: "0.875rem" }}>
                    {content}
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {Array.isArray(content) && content.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          color: "#334155",
                          fontSize: "0.875rem",
                          lineHeight: 1.65,
                          "& ::marker": { color: accent },
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "#64748B", fontStyle: "italic" }}>
              * Professional icons used for better visualization.
            </Typography>
          </Box>

          {/* Companies section */}
          {detail.companySuggestions && (
            <Box
              sx={{
                borderRadius: "14px",
                border: "1px solid #FDE68A",
                background: "rgba(245,158,11,0.05)",
                p: { xs: 2, md: 2.5 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <BusinessIcon sx={{ fontSize: "1.2rem", color: "#B45309" }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, color: "#B45309", fontSize: "0.8rem", letterSpacing: "0.02em" }}
                >
                  Companies That Hire for This Skill Stack
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {detail.companySuggestions.map((company) => (
                  <Box
                    key={company}
                    component="span"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "20px",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      background: "#FFFBEB",
                      color: "#92400E",
                      border: "1px solid #FDE68A",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {company}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/* ------------------------------- Component ------------------------------ */

export default function TenantBootcampEnrollment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isAlreadyEnrolled, setIsAlreadyEnrolled] = useState(false);
  const [existingEnrollment, setExistingEnrollment] = useState(null);

  const profile = readStudentProfile();

  const [form, setForm] = useState(() => ({
    userId:         profile.userId || localStorage.getItem("user_id") || "",
    fullName:       profile.fullName || "",
    rollNumber:     profile.rollNumber || "",   // display-only; not sent to API
    track:          "",
    experienceLevel: "0-2 years",               // default for students
    technology:     "",
    techBunch:      "",
    role:           "",
    certification:  "",
    githubUsername: "",
    branch:         profile.branch || "",
    interestedRole: "",
    interestedSkill: "",
    prevSkills:     "",
  }));

  /* Sync userId + profile once localStorage is guaranteed available */
  useEffect(() => {
    const p = readStudentProfile();
    const uid = p.userId || localStorage.getItem("user_id") || "";
    
    setForm((prev) => ({
      ...prev,
      userId:     uid || prev.userId,
      fullName:   p.fullName || prev.fullName,
      rollNumber: p.rollNumber || prev.rollNumber,
      branch:     p.branch || prev.branch,
    }));

    if (uid) {
      fetchExistingEnrollment(uid);
      fetchGitHubStatus(uid);
    }
  }, []);

  const fetchGitHubStatus = async (uid) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/github/status?userId=${uid}`);
      const data = await res.json();
      if (data.ok) {
        const username = data.github_username || data.githubUsername;
        if (username) {
          setForm((prev) => ({
            ...prev,
            githubUsername: username,
          }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch GitHub status:", err);
    }
  };

  const fetchExistingEnrollment = async (uid) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/my-enrollment?userId=${uid}`);
      const data = await res.json();
      if (data.ok && data.enrollment) {
        setExistingEnrollment(data.enrollment);
        // Pre-fill form with existing data if needed, or just lock it
        setForm(prev => ({
          ...prev,
          track: data.enrollment.track,
          techBunch: data.enrollment.techBunch || "",
          role: data.enrollment.role || "",
          technology: data.enrollment.technology || "",
          certification: data.enrollment.certification || "",
          githubUsername: data.enrollment.githubUsername || prev.githubUsername || "",
          experienceLevel: data.enrollment.experienceLevel || "0-2 years",
          branch: data.enrollment.branch || prev.branch,
        }));
      }
    } catch (err) {
      console.error("Failed to fetch existing enrollment:", err);
    }
  };

  /* Restore cached track-specific selections */
  const applyCachedForTrack = (prev, trackValue) => {
    try {
      const raw = localStorage.getItem(enKey(prev.userId, trackValue));
      if (!raw) return prev;
      const enr = JSON.parse(raw);
      return {
        ...prev,
        technology:    trackValue === "technologies" ? (enr.technology || "")    : "",
        techBunch:     trackValue === "techbunch"    ? (enr.techBunch || "")     : "",
        role:          trackValue === "roles"        ? (enr.role || "")          : "",
        certification: trackValue === "certified"   ? (enr.certification || "") : "",
        githubUsername: (trackValue === "roles" || trackValue === "techbunch") ? (enr.githubUsername || prev.githubUsername || "") : "",
      };
    } catch {
      return prev;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "track") {
      setErrors({});
      return setForm((prev) => {
        const cleared = {
          ...prev,
          track: value,
          technology: "", techBunch: "", role: "",
          certification: "", 
        };
        const restored = applyCachedForTrack(cleared, value);
        return restored;
      });
    }

    if (name === "githubUsername") {
      return setForm((prev) => ({ ...prev, githubUsername: value.trim() }));
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  /* ------- Validation ------- */
  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = "Full Name is required.";
    if (!form.track)           errs.track = "Please select a Skill Track.";
    if (form.track && !isTrackComplete(form)) {
      errs.trackSelection = "Please complete the required selection for the chosen track.";
    }
    // Guard: only approved student-tenant roles are accepted
    if (form.track === "roles" && form.role && !STUDENT_TENANT_ROLE_VALUES.has(form.role)) {
      errs.trackSelection = "Selected role is not available for this enrollment. Please choose a valid role.";
    }
    if (form.track === "roles" || form.track === "techbunch") {
      if (!form.githubUsername) {
        errs.githubUsername = "GitHub Username is required.";
      } else if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/.test(form.githubUsername)) {
        errs.githubUsername = "Invalid GitHub username (alphanumeric / hyphens, 1–39 chars).";
      }
    }
    return errs;
  };

  /* ------- Submit ------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const latestUserId =
      localStorage.getItem("user_id") ||
      form.userId ||
      readStudentProfile().userId ||
      "";

    if (!latestUserId) {
      setErrors({ submit: "Session expired. Please log in again." });
      return;
    }

    // Build payload - only student-relevant fields, no professional fields
    let skillSlug = null;
    if (form.track === "techbunch") {
      const selectedBunch = TECH_BUNCH_TRACKS.find((t) => t.skillSet === form.techBunch);
      if (selectedBunch) skillSlug = selectedBunch.folder;
    } else if (form.track === "roles") {
      const selectedRole = STUDENT_TENANT_ROLES.find((r) => r.value === form.role);
      if (selectedRole) skillSlug = selectedRole.jsonFile;
    }

    const payload = {
      userId:          String(latestUserId),
      fullName:        form.fullName.trim(),
      track:           form.track,
      experienceLevel: form.experienceLevel,
      branch:          form.branch,
      interestedRole:  form.interestedRole  || null,
      areaOfInterest:  form.interestedSkill || null,   // maps to area_of_interest column
      prevSkills:      form.prevSkills      || null,
      // Track-specific
      technology:      form.technology      || null,
      techBunch:       form.techBunch       || null,
      role:            form.role            || null,
      skillSlug:       skillSlug,
      workMode:        "single",
      certification:   form.certification   || null,
      githubUsername:  (form.track === "roles" || form.track === "techbunch") ? (form.githubUsername || null) : null,
      // Removed fields - send placeholder values to satisfy backend NOT NULL constraints
      phone:           "N/A",      // phone removed from student tenant form
      dob:             "N/A",
      gender:          "N/A",
      address:         "N/A",
      working:         "N/A",
      company:         "N/A",
    };

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.status === 409 || (data && data.code === "ALREADY_ENROLLED")) {
        setIsAlreadyEnrolled(true);
        setErrors({ submit: data.error || data.message || "You have already registered check in my bootcamps" });
        setLoading(false);
        // Ensure scroll to error
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        return;
      }

      if (!res.ok) {
        setErrors({ submit: data.error || data.message || `HTTP ${res.status}` });
        setLoading(false);
        return;
      }

      const { enrollment } = data;
      try {
        localStorage.setItem(
          enKey(enrollment.user_id, enrollment.track),
          JSON.stringify(enrollment)
        );
      } catch {}

      navigate("/bootcamp/my-bootcamps");
    } catch (err) {
      setErrors({ submit: `Enrollment failed: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  /* ------- Track-specific selection fields ------- */
  const renderTrackFields = () => {
    switch (form.track) {
    case "techbunch":
        return (
          <>
            <Box sx={{ mt: 4, mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#334155",
                  mb: 1,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  fontSize: "0.95rem",
                  letterSpacing: "0.01em"
                }}
              >
                <TerminalIcon sx={{ fontSize: 20, color: "#6366F1" }} />
                Specialization Path
              </Typography>
            </Box>
            <SelectField
              required
              name="techBunch"
              label="Choose Engineering Stack"
              value={form.techBunch}
              onChange={handleChange}
              placeholder="- Select a stack -"
            >
              {TECH_BUNCH_TRACKS.map(({ title, skillSet }) => (
                <MenuItem key={skillSet} value={skillSet}>
                  {title}
                </MenuItem>
              ))}
            </SelectField>

            {form.techBunch && (
              <TechBunchDetailCard techBunch={form.techBunch} />
            )}

            <TextField
              required
              fullWidth
              name="githubUsername"
              label="GitHub Username"
              value={form.githubUsername}
              onChange={handleChange}
              margin="normal"
              sx={sxField}
              error={!!errors.githubUsername}
              helperText={errors.githubUsername || "Used for collaborative projects and portfolio building"}
            />
            <TrackLockWarning />
          </>
        );

    case "roles":
        return (
          <>
            <Box sx={{ mt: 4, mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#334155",
                  mb: 1,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  fontSize: "0.95rem",
                  letterSpacing: "0.01em"
                }}
              >
                <RocketIcon sx={{ fontSize: 20, color: "#8B5CF6" }} />
                Target Career Role
              </Typography>
            </Box>
            <SelectField
              required
              name="role"
              label="Select Industry Role"
              value={form.role}
              onChange={handleChange}
              placeholder="- Select role -"
            >
              {STUDENT_TENANT_ROLES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </SelectField>

            {form.role && (
              <IndustryRoleDetailCard role={form.role} />
            )}

            <TextField
              required
              fullWidth
              name="githubUsername"
              label="GitHub Username"
              value={form.githubUsername}
              onChange={handleChange}
              margin="normal"
              sx={sxField}
              error={!!errors.githubUsername}
              helperText={errors.githubUsername || "Used for collaborative projects and portfolio building"}
            />
            <TrackLockWarning />
          </>
        );

      default:
        return (
          <Box sx={{ py: 4, textAlign: "center", opacity: 0.5 }}>
            <Typography variant="body2">Please select a track to see options</Typography>
          </Box>
        );
    }
  };

  /* ------- Render ------- */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#E3F2FD",
        color: "var(--text-body)",
        py: 8,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        component="form"
        onSubmit={isAlreadyEnrolled ? (e) => { e.preventDefault(); navigate("/bootcamp/my-bootcamps"); } : handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 750,
          backgroundColor: "#FFFFFF",
          p: { xs: 3, md: 6 },
          borderRadius: 8,
          boxShadow: "0 20px 50px rgba(31, 138, 203, 0.15)",
          border: "1px solid var(--accent-gray)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background glow */}
        <Box
          sx={{
            position: "absolute", top: -100, right: -100,
            width: 250, height: 250, borderRadius: "50%",
            background: "rgba(43, 176, 230, 0.05)", filter: "blur(60px)",
          }}
        />

        {/* Header */}
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 900, color: "var(--text-heading)",
            textTransform: "uppercase", letterSpacing: "-0.02em", mb: 1,
          }}
        >
          Bootcamp{" "}
          <span style={{ color: "var(--primary-blue)" }}>Enrollment</span>
        </Typography>
        <Typography
          variant="h6"
          align="center"
          sx={{ mb: 2, color: "var(--text-muted)", fontWeight: 500 }}
        >
          Begin your journey into specialized industry training
        </Typography>

        {/* Student badge */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Chip
            label="Student Enrollment Form"
            size="small"
            sx={{
              backgroundColor: "#EBF5FF",
              color: "var(--primary-blue)",
              fontWeight: 700,
              border: "1px solid var(--primary-blue)",
            }}
          />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* -- Auto-filled profile fields (read-only) -- */}
          <Typography
            variant="subtitle2"
            sx={{ color: "var(--text-muted)", mb: 1, fontWeight: 600 }}
          >
            STUDENT PROFILE
          </Typography>

          <TextField
            fullWidth
            label="Full Name"
            value={form.fullName}
            margin="normal"
            sx={sxReadOnly}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Auto-filled from your student record"
          />

          <TextField
            fullWidth
            label="Roll Number"
            value={form.rollNumber}
            margin="normal"
            sx={sxReadOnly}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Auto-filled from your student record"
          />

          {/* -- Academic fields -- */}
          <Typography
            variant="subtitle2"
            sx={{ color: "var(--text-muted)", mt: 3, mb: 1, fontWeight: 600 }}
          >
            ACADEMIC DETAILS
          </Typography>

          {/* Branch */}
          <TextField
            fullWidth
            label="Branch / Department"
            value={form.branch}
            margin="normal"
            sx={sxReadOnly}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Auto-filled from your student record"
          />
          {errors.branch && (
            <Typography variant="caption" color="error">{errors.branch}</Typography>
          )}

          {/* Experience - fixed at 0-2 years for student tenant enrollment */}
          <TextField
            fullWidth
            label="Level of Experience"
            value="0-2 years"
            margin="normal"
            sx={sxReadOnly}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Fixed for student enrollment"
          />

          {/* -- Interests & Skills -- */}
          <Typography
            variant="subtitle2"
            sx={{ color: "var(--text-muted)", mt: 3, mb: 1, fontWeight: 600 }}
          >
            INTERESTS & SKILLS
          </Typography>

          <TextField
            fullWidth
            name="interestedRole"
            label="Interested Role"
            value={form.interestedRole}
            onChange={handleChange}
            margin="normal"
            sx={sxField}
            placeholder="e.g. Full Stack Developer, Data Scientist"
          />

          <SelectField
            name="interestedSkill"
            label="Interested Skill"
            value={form.interestedSkill}
            onChange={handleChange}
            placeholder="- Select a skill -"
          >
            {SKILL_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </SelectField>

          <TextField
            fullWidth
            name="prevSkills"
            label="Previous Skills / Technologies Known"
            value={form.prevSkills}
            onChange={handleChange}
            margin="normal"
            sx={sxField}
            placeholder="e.g. HTML, CSS, Basic Python"
            multiline
            rows={2}
          />

          {/* -- Bootcamp track -- */}
          <Typography
            variant="subtitle2"
            sx={{ color: "var(--text-muted)", mt: 4, mb: 1, fontWeight: 700, letterSpacing: "0.05em" }}
          >
            SKILL TRACK SELECTION
          </Typography>

          <TrackSelectionCards
            value={form.track}
            onChange={(val) => setForm((f) => ({ ...f, track: val }))}
            disabled={false}
            existingTrack={null}
          />

          {errors.track && (
            <Typography variant="caption" color="error" sx={{ mt: -2, mb: 2, display: "block" }}>
              {errors.track}
            </Typography>
          )}

          {/* Track-specific fields */}
          {renderTrackFields()}
          {errors.trackSelection && (
            <Typography variant="caption" color="error">{errors.trackSelection}</Typography>
          )}

          {/* -- Submit error -- */}
          {errors.submit && (
            <Box
              sx={{
                mt: 2, p: 2, borderRadius: 2,
                backgroundColor: isAlreadyEnrolled ? "#EFF6FF" : "#FEE2E2",
                border: isAlreadyEnrolled ? "1px solid #BFDBFE" : "1px solid #FCA5A5",
              }}
            >
              <Typography variant="subtitle2" color={isAlreadyEnrolled ? "primary" : "error"} sx={{ fontWeight: 700, mb: 0.5 }}>
                {isAlreadyEnrolled ? "Already Registered" : "Error"}
              </Typography>
              <Typography variant="body2" color={isAlreadyEnrolled ? "textPrimary" : "error"}>
                {errors.submit}
              </Typography>
              {isAlreadyEnrolled && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/bootcamp/my-bootcamps")}
                  sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
                >
                  Go to My Bootcamps
                </Button>
              )}
            </Box>
          )}

          {/* -- Submit button -- */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 5,
              py: 2,
              borderRadius: 3,
              fontWeight: 800,
              fontSize: "1rem",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              background: isAlreadyEnrolled 
                ? "linear-gradient(135deg, #94A3B8, #64748B)" 
                : "linear-gradient(135deg, var(--primary-blue), #1565C0)",
              boxShadow: isAlreadyEnrolled ? "none" : "0 8px 24px rgba(21, 101, 192, 0.35)",
              "&:hover": {
                background: isAlreadyEnrolled 
                  ? "linear-gradient(135deg, #94A3B8, #64748B)" 
                  : "linear-gradient(135deg, #1565C0, var(--primary-blue))",
                boxShadow: isAlreadyEnrolled ? "none" : "0 12px 32px rgba(21, 101, 192, 0.45)",
              },
              "&:disabled": { opacity: 0.7 },
            }}
          >
            {loading ? "Enrolling..." : isAlreadyEnrolled ? "View My Bootcamps" : "Enroll in Bootcamp ->"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}